import moment from 'moment-timezone';
import * as XLSX from 'xlsx';

const EMPTY_ARRAY = [];
const BAY_TOKEN_REGEX = /\b\d{1,3}[A-Z]?\b/g;

const toSafeString = (value) => (value === null || value === undefined ? '' : `${value}`.trim());

const isBlankRow = (row) => {
  if (!Array.isArray(row)) return true;
  return row.every((cell) => toSafeString(cell) === '');
};

const normalizeHeader = (value) => toSafeString(value).toUpperCase().replace(/\s+/g, ' ');

const normalizeFlightNo = (value) => toSafeString(value).replace(/\s+/g, ' ').toUpperCase();

export const normalizeBayKey = (value) => {
  const raw = toSafeString(value).toUpperCase();
  if (!raw) return '';
  const noPrefix = raw.replace(/^BAY\s*/i, '');
  return noPrefix.replace(/[^A-Z0-9]/g, '');
};

const unique = (items) => Array.from(new Set((items || []).filter(Boolean)));

const asMomentFromExcelSerial = (serial, timezone) => {
  const num = Number(serial);
  if (!Number.isFinite(num)) return null;
  const parsed = XLSX?.SSF?.parse_date_code ? XLSX.SSF.parse_date_code(num) : null;
  if (!parsed) return null;

  const second = Math.floor(parsed.S || 0);
  const millisecond = Math.round(((parsed.S || 0) - second) * 1000);
  return moment.tz(
    {
      year: parsed.y,
      month: Math.max(0, (parsed.m || 1) - 1),
      day: parsed.d || 1,
      hour: parsed.H || 0,
      minute: parsed.M || 0,
      second,
      millisecond,
    },
    timezone
  );
};

const asMomentFromDateObject = (value, timezone) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return null;
  return moment.tz(
    {
      year: value.getFullYear(),
      month: value.getMonth(),
      day: value.getDate(),
      hour: value.getHours(),
      minute: value.getMinutes(),
      second: value.getSeconds(),
      millisecond: value.getMilliseconds(),
    },
    timezone
  );
};

const parseDateOnly = (value, timezone) => {
  if (value === null || value === undefined || value === '') return null;

  if (typeof value === 'number') {
    const m = asMomentFromExcelSerial(value, timezone);
    return m && m.isValid() ? m.startOf('day') : null;
  }

  if (value instanceof Date) {
    const m = asMomentFromDateObject(value, timezone);
    return m && m.isValid() ? m.startOf('day') : null;
  }

  const text = toSafeString(value);
  if (!text) return null;

  const formats = [
    'DD.MM.YYYY',
    'D.M.YYYY',
    'DD/MM/YYYY',
    'D/M/YYYY',
    'YYYY-MM-DD',
    'DD-MM-YYYY',
    'D-M-YYYY',
  ];

  let parsed = moment.tz(text, formats, timezone, true);
  if (!parsed.isValid()) parsed = moment.tz(text, timezone);
  if (!parsed.isValid()) return null;
  return parsed.startOf('day');
};

const parseTimeOnDate = (timeText, dateMoment, timezone) => {
  if (!dateMoment || !dateMoment.isValid()) return null;
  const text = toSafeString(timeText);
  if (!text) return null;

  const formats = ['HH:mm:ss', 'H:mm:ss', 'HH:mm', 'H:mm', 'hh:mm A', 'h:mm A'];
  const parsed = moment.tz(text, formats, timezone, true);
  if (!parsed.isValid()) return null;

  return dateMoment
    .clone()
    .hour(parsed.hour())
    .minute(parsed.minute())
    .second(parsed.second())
    .millisecond(0);
};

const parseDateTime = (value, timezone, fallbackDate) => {
  if (value === null || value === undefined || value === '') return null;

  if (typeof value === 'number') {
    const parsed = asMomentFromExcelSerial(value, timezone);
    if (!parsed || !parsed.isValid()) return null;

    if (parsed.year() <= 1900 && fallbackDate && fallbackDate.isValid()) {
      return fallbackDate
        .clone()
        .hour(parsed.hour())
        .minute(parsed.minute())
        .second(parsed.second())
        .millisecond(parsed.millisecond());
    }

    return parsed;
  }

  if (value instanceof Date) {
    const parsed = asMomentFromDateObject(value, timezone);
    if (!parsed || !parsed.isValid()) return null;
    return parsed;
  }

  const text = toSafeString(value);
  if (!text) return null;

  const datetimeFormats = [
    'DD.MM.YYYY HH:mm:ss',
    'DD.MM.YYYY HH:mm',
    'DD/MM/YYYY HH:mm:ss',
    'DD/MM/YYYY HH:mm',
    'YYYY-MM-DD HH:mm:ss',
    'YYYY-MM-DD HH:mm',
    'DD-MM-YYYY HH:mm:ss',
    'DD-MM-YYYY HH:mm',
    'DD.MM.YYYY hh:mm A',
    'DD/MM/YYYY hh:mm A',
  ];

  let parsed = moment.tz(text, datetimeFormats, timezone, true);
  if (parsed.isValid()) return parsed;

  const timeOnly = parseTimeOnDate(text, fallbackDate, timezone);
  if (timeOnly) return timeOnly;

  parsed = moment.tz(text, timezone);
  if (!parsed.isValid()) return null;

  if (fallbackDate && fallbackDate.isValid() && /^\d{1,2}:\d{2}/.test(text)) {
    return fallbackDate
      .clone()
      .hour(parsed.hour())
      .minute(parsed.minute())
      .second(parsed.second())
      .millisecond(parsed.millisecond());
  }

  return parsed;
};

const getColumnIndexes = (headerRow) => {
  const map = {};
  (headerRow || []).forEach((cell, idx) => {
    const key = normalizeHeader(cell);
    if (!key) return;
    if (!map[key]) map[key] = [];
    map[key].push(idx);
  });

  return {
    arrDateIdx: map.DATE?.[0] ?? null,
    depDateIdx: map.DATE?.[1] ?? map.DATE?.[0] ?? null,
    arrFlightIdx: map['ARR FLIGHT NO']?.[0] ?? null,
    depFlightIdx: map['DEP FLIGHT NO']?.[0] ?? null,
    ataIdx: map.ATA?.[0] ?? null,
    atdIdx: map.ATD?.[0] ?? null,
    arrBayIdx: map['BAY NO']?.[0] ?? null,
    depBayIdx: map['BAY NO']?.[1] ?? map['BAY NO']?.[0] ?? null,
    airportIdx: map['AIRPORT NAME']?.[0] ?? null,
  };
};

const detectHeaderRowIndex = (rows) => {
  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i] || [];
    const normalized = row.map((cell) => normalizeHeader(cell));
    const hasArr = normalized.includes('ARR FLIGHT NO');
    const hasDep = normalized.includes('DEP FLIGHT NO');
    if (hasArr && hasDep) return i;
  }
  return -1;
};

export const parseFlightScheduleRows = ({ rows, templateId, timezone, fileName, importBatchId }) => {
  const headerRowIndex = detectHeaderRowIndex(rows || []);
  if (headerRowIndex < 0) {
    throw new Error('Could not find header row (ARR Flight No / DEP Flight No)');
  }

  const headerRow = rows[headerRowIndex] || [];
  const idx = getColumnIndexes(headerRow);

  if (idx.arrFlightIdx === null || idx.depFlightIdx === null || idx.ataIdx === null || idx.atdIdx === null) {
    throw new Error('Flight sheet headers are incomplete');
  }

  const flights = [];
  const rejected = [];
  const airports = new Set();
  const bayKeySet = new Set();
  const localDates = new Set();

  let rowsParsed = 0;

  for (let r = headerRowIndex + 1; r < (rows || []).length; r += 1) {
    const row = rows[r] || [];
    if (isBlankRow(row)) continue;
    rowsParsed += 1;

    const arrDate = parseDateOnly(row[idx.arrDateIdx], timezone);
    const depDate = parseDateOnly(row[idx.depDateIdx], timezone);

    const fallbackAtaDate = arrDate || depDate;
    const ata = parseDateTime(row[idx.ataIdx], timezone, fallbackAtaDate);

    const fallbackAtdDate = depDate || arrDate || (ata ? ata.clone().startOf('day') : null);
    const atdRaw = parseDateTime(row[idx.atdIdx], timezone, fallbackAtdDate);
    const atd = atdRaw && ata && atdRaw.isBefore(ata) ? atdRaw.clone().add(1, 'day') : atdRaw;

    const arrFlightNo = normalizeFlightNo(row[idx.arrFlightIdx]);
    const depFlightNo = normalizeFlightNo(row[idx.depFlightIdx]);

    const arrBayRaw = toSafeString(row[idx.arrBayIdx]);
    const depBayRaw = toSafeString(row[idx.depBayIdx]);
    const arrBayKey = normalizeBayKey(arrBayRaw);
    const depBayKey = normalizeBayKey(depBayRaw);

    const bayKeys = unique([arrBayKey, depBayKey]);

    if (!ata || !ata.isValid() || !atd || !atd.isValid() || !arrFlightNo || !depFlightNo || bayKeys.length === 0) {
      rejected.push({
        rowNumber: r + 1,
        reason: 'Missing required fields',
      });
      continue;
    }

    const localDate = ata.clone().format('YYYY-MM-DD');
    const airportName = toSafeString(row[idx.airportIdx]) || 'Unknown Airport';

    localDates.add(localDate);
    airports.add(airportName);
    bayKeys.forEach((key) => bayKeySet.add(key));

    flights.push({
      id: `flight_${templateId}_${importBatchId}_${r + 1}`,
      templateId,
      importBatchId,
      sourceFileName: fileName,
      sourceRowNumber: r + 1,
      airportName,
      localDate,
      arrFlightNo,
      depFlightNo,
      ataLocal: ata.format('YYYY-MM-DD HH:mm:ss'),
      atdLocal: atd.format('YYYY-MM-DD HH:mm:ss'),
      ataTsUtc: ata.clone().utc().valueOf(),
      atdTsUtc: atd.clone().utc().valueOf(),
      arrBayRaw,
      depBayRaw,
      bayKeys,
      bayTransitionUnknown: bayKeys.length > 1,
    });
  }

  const orderedDates = Array.from(localDates).sort();

  return {
    flights,
    rejected,
    summary: {
      rowsParsed,
      validRows: flights.length,
      rejectedRows: rejected.length,
      airports: Array.from(airports).sort(),
      dateFrom: orderedDates[0] || null,
      dateTo: orderedDates[orderedDates.length - 1] || null,
      bayKeys: Array.from(bayKeySet).sort(),
      bayKeyCount: bayKeySet.size,
    },
  };
};

const fileToArrayBuffer = async (file) => {
  if (!file) throw new Error('File is required');
  if (typeof file.arrayBuffer === 'function') return file.arrayBuffer();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
};

export const parseFlightScheduleFile = async ({ file, templateId, timezone }) => {
  const fileName = file?.name || 'flight_schedule';
  const importBatchId = `flight_import_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

  const buffer = await fileToArrayBuffer(file);
  const workbook = XLSX.read(buffer, {
    type: 'array',
    raw: true,
    cellDates: false,
  });

  const firstSheetName = workbook.SheetNames?.[0];
  if (!firstSheetName) {
    throw new Error('No sheet found in file');
  }

  const worksheet = workbook.Sheets[firstSheetName];
  const rows = XLSX.utils.sheet_to_json(worksheet, {
    header: 1,
    raw: true,
    defval: null,
    blankrows: false,
  });

  const parsed = parseFlightScheduleRows({
    rows,
    templateId,
    timezone,
    fileName,
    importBatchId,
  });

  return {
    ...parsed,
    importBatchId,
    fileName,
  };
};

export const extractBayTokensFromZoneName = (zoneName) => {
  const text = toSafeString(zoneName).toUpperCase();
  if (!text) return EMPTY_ARRAY;
  const hits = text.match(BAY_TOKEN_REGEX) || [];
  return unique(hits.map((item) => normalizeBayKey(item)));
};

const zoneMapById = (zones) => {
  const map = {};
  (zones || []).forEach((zone) => {
    map[zone.id] = zone;
  });
  return map;
};

export const computeAutoBayMappings = ({ bayKeys, zones }) => {
  const tokenToZones = {};
  (zones || []).forEach((zone) => {
    const tokens = extractBayTokensFromZoneName(zone?.name);
    tokens.forEach((token) => {
      if (!tokenToZones[token]) tokenToZones[token] = [];
      tokenToZones[token].push(zone.id);
    });
  });

  const result = {};
  (bayKeys || []).forEach((rawBayKey) => {
    const bayKey = normalizeBayKey(rawBayKey);
    if (!bayKey) return;

    const candidates = unique(tokenToZones[bayKey] || []);
    let status = 'unmapped';
    let confidence = 'low';
    if (candidates.length === 1) {
      status = 'mapped';
      confidence = 'high';
    } else if (candidates.length > 1) {
      status = 'ambiguous';
      confidence = 'low';
    }

    result[bayKey] = {
      bayKey,
      autoZoneIds: candidates,
      status,
      confidence,
    };
  });

  return result;
};

export const buildEffectiveBayMappings = ({ bayKeys, zones, manualMappings }) => {
  const zoneById = zoneMapById(zones);
  const manualByKey = {};

  (manualMappings || []).forEach((mapping) => {
    const key = normalizeBayKey(mapping?.bayKey);
    if (!key) return;
    manualByKey[key] = mapping;
  });

  const auto = computeAutoBayMappings({ bayKeys, zones });

  const rows = (bayKeys || [])
    .map((rawBayKey) => normalizeBayKey(rawBayKey))
    .filter(Boolean)
    .map((bayKey) => {
      const autoInfo = auto[bayKey] || { autoZoneIds: [] };
      const manual = manualByKey[bayKey] || null;
      const manualZoneId = manual?.zoneId && zoneById[manual.zoneId] ? manual.zoneId : null;

      let effectiveZoneIds = [];
      let status = 'unmapped';
      let method = 'auto';
      let confidence = autoInfo.confidence || 'low';

      if (manualZoneId) {
        effectiveZoneIds = [manualZoneId];
        status = 'mapped';
        method = 'manual';
        confidence = 'high';
      } else if ((autoInfo.autoZoneIds || []).length === 1) {
        effectiveZoneIds = autoInfo.autoZoneIds;
        status = 'mapped';
        method = 'auto';
        confidence = 'high';
      } else if ((autoInfo.autoZoneIds || []).length > 1) {
        status = 'ambiguous';
        method = 'auto';
        confidence = 'low';
      }

      return {
        bayKey,
        autoZoneIds: autoInfo.autoZoneIds || [],
        autoZoneNames: (autoInfo.autoZoneIds || []).map((id) => zoneById[id]?.name || id),
        manualZoneId,
        manualZoneName: manualZoneId ? (zoneById[manualZoneId]?.name || manualZoneId) : null,
        effectiveZoneIds,
        effectiveZoneNames: effectiveZoneIds.map((id) => zoneById[id]?.name || id),
        status,
        method,
        confidence,
      };
    })
    .sort((a, b) => a.bayKey.localeCompare(b.bayKey));

  const byBayKey = {};
  rows.forEach((row) => {
    byBayKey[row.bayKey] = row;
  });

  return {
    rows,
    byBayKey,
  };
};

const buildChannelIntervals = ({ events, enterType, exitType, thresholdSec }) => {
  const ordered = (events || [])
    .filter((event) => event && (event.eventType === enterType || event.eventType === exitType))
    .filter((event) => Number.isFinite(Number(event.startTs)) && event.assetId && event.zoneId)
    .sort((a, b) => Number(a.startTs) - Number(b.startTs));

  const activeByKey = {};
  const intervals = [];

  ordered.forEach((event) => {
    const key = `${event.assetId}::${event.zoneId}`;

    if (event.eventType === enterType) {
      activeByKey[key] = {
        startTs: Number(event.startTs),
        enterEventId: event.id,
      };
      return;
    }

    const open = activeByKey[key];
    if (!open) return;

    const endTs = Number(event.startTs);
    const durationSec = Math.max(0, Math.round((endTs - open.startTs) / 1000));

    if (durationSec >= thresholdSec) {
      intervals.push({
        assetId: event.assetId,
        assetCode: event.assetCode,
        zoneId: event.zoneId,
        startTs: open.startTs,
        endTs,
        durationSec,
        enterEventId: open.enterEventId,
        exitEventId: event.id,
      });
    }

    delete activeByKey[key];
  });

  return intervals;
};

const groupIntervals = (intervals) => {
  const grouped = {};
  (intervals || []).forEach((interval) => {
    if (!grouped[interval.assetId]) grouped[interval.assetId] = {};
    if (!grouped[interval.assetId][interval.zoneId]) grouped[interval.assetId][interval.zoneId] = [];
    grouped[interval.assetId][interval.zoneId].push(interval);
  });
  return grouped;
};

const overlapSummary = (intervals, startTs, endTs) => {
  const ordered = (intervals || []).slice().sort((a, b) => a.startTs - b.startTs);
  let overlapMs = 0;
  let firstOverlapTs = null;
  let lastOverlapTs = null;
  const evidence = [];

  ordered.forEach((interval) => {
    const overlapStart = Math.max(startTs, Number(interval.startTs || 0));
    const overlapEnd = Math.min(endTs, Number(interval.endTs || 0));
    if (overlapEnd <= overlapStart) return;

    const delta = overlapEnd - overlapStart;
    overlapMs += delta;
    firstOverlapTs = firstOverlapTs === null ? overlapStart : Math.min(firstOverlapTs, overlapStart);
    lastOverlapTs = lastOverlapTs === null ? overlapEnd : Math.max(lastOverlapTs, overlapEnd);
    evidence.push({ enterEventId: interval.enterEventId, exitEventId: interval.exitEventId });
  });

  if (overlapMs <= 0) return null;

  return {
    overlapSec: Math.round(overlapMs / 1000),
    firstOverlapTs,
    lastOverlapTs,
    evidence,
  };
};

const pickBestZone = (assetZones, zoneIds, startTs, endTs) => {
  const candidates = [];

  (zoneIds || []).forEach((zoneId) => {
    const list = assetZones?.[zoneId] || EMPTY_ARRAY;
    const summary = overlapSummary(list, startTs, endTs);
    if (!summary) return;
    candidates.push({
      zoneId,
      ...summary,
    });
  });

  if (candidates.length === 0) return null;

  candidates.sort((a, b) => {
    if (b.overlapSec !== a.overlapSec) return b.overlapSec - a.overlapSec;
    if (a.firstOverlapTs !== b.firstOverlapTs) return a.firstOverlapTs - b.firstOverlapTs;
    return `${a.zoneId}`.localeCompare(`${b.zoneId}`);
  });

  return candidates[0];
};

const normalizeAssetSet = (events, assetIds) => {
  const byId = {};
  (events || []).forEach((event) => {
    if (!event?.assetId) return;
    if (!byId[event.assetId]) {
      byId[event.assetId] = {
        assetId: event.assetId,
        assetCode: event.assetCode || event.assetId,
      };
    }
  });

  if (Array.isArray(assetIds) && assetIds.length > 0) {
    const filtered = {};
    assetIds.forEach((assetId) => {
      if (byId[assetId]) filtered[assetId] = byId[assetId];
    });
    return filtered;
  }

  return byId;
};

export const computeFlightContextFromData = ({
  template,
  dateKey,
  events,
  flights,
  manualMappings,
  assetIds,
  channelMode = 'polygon_priority',
}) => {
  const zones = template?.zones || [];
  const zoneById = zoneMapById(zones);
  const thresholdSec = Math.max(0, Number(template?.rules?.dwellThresholdSec || 120));

  const flightRows = (flights || []).filter((flight) => !dateKey || flight.localDate === dateKey);
  const allBayKeys = unique(flightRows.flatMap((flight) => flight.bayKeys || []));
  const effectiveMappings = buildEffectiveBayMappings({
    bayKeys: allBayKeys,
    zones,
    manualMappings,
  });

  const polygonIntervals = buildChannelIntervals({
    events,
    enterType: 'ENTER',
    exitType: 'EXIT',
    thresholdSec,
  });

  const serviceIntervals = buildChannelIntervals({
    events,
    enterType: 'SERVICE_ENTER',
    exitType: 'SERVICE_EXIT',
    thresholdSec,
  });

  const polygonByAssetZone = groupIntervals(polygonIntervals);
  const serviceByAssetZone = groupIntervals(serviceIntervals);
  const assetMap = normalizeAssetSet(events, assetIds);

  const matches = [];
  const flightsOut = [];

  flightRows.forEach((flight) => {
    const mappedZones = unique(
      (flight.bayKeys || []).flatMap((bayKey) => effectiveMappings.byBayKey[bayKey]?.effectiveZoneIds || EMPTY_ARRAY)
    );

    const mappedZoneNames = mappedZones.map((zoneId) => zoneById[zoneId]?.name || zoneId);
    const flightMatches = [];

    Object.values(assetMap).forEach((asset) => {
      const startTs = Number(flight.ataTsUtc || 0);
      const endTs = Number(flight.atdTsUtc || 0);
      if (!Number.isFinite(startTs) || !Number.isFinite(endTs) || endTs <= startTs) return;

      const polygonBest = pickBestZone(polygonByAssetZone[asset.assetId], mappedZones, startTs, endTs);
      const serviceBest = pickBestZone(serviceByAssetZone[asset.assetId], mappedZones, startTs, endTs);

      let selected = null;
      let channel = null;

      if (channelMode === 'polygon_priority') {
        if (polygonBest) {
          selected = polygonBest;
          channel = 'polygon';
        } else if (serviceBest) {
          selected = serviceBest;
          channel = 'service';
        }
      } else if (polygonBest || serviceBest) {
        const both = [
          polygonBest ? { ...polygonBest, channel: 'polygon' } : null,
          serviceBest ? { ...serviceBest, channel: 'service' } : null,
        ].filter(Boolean);

        both.sort((a, b) => {
          if (b.overlapSec !== a.overlapSec) return b.overlapSec - a.overlapSec;
          if (a.firstOverlapTs !== b.firstOverlapTs) return a.firstOverlapTs - b.firstOverlapTs;
          return `${a.zoneId}`.localeCompare(`${b.zoneId}`);
        });

        selected = both[0] || null;
        channel = selected?.channel || null;
      }

      if (!selected || !channel) return;

      const match = {
        id: `equip_flight_${flight.id}_${asset.assetId}`,
        templateId: template?.id,
        date: flight.localDate,
        flightId: flight.id,
        assetId: asset.assetId,
        assetCode: asset.assetCode,
        matchChannel: channel,
        matchedZoneId: selected.zoneId,
        matchedZoneName: zoneById[selected.zoneId]?.name || selected.zoneId,
        overlapSec: selected.overlapSec,
        firstOverlapTs: selected.firstOverlapTs,
        lastOverlapTs: selected.lastOverlapTs,
        policyApplied: 'polygon_priority',
        evidence: selected.evidence,
      };

      matches.push(match);
      flightMatches.push(match);
    });

    const mappingStatuses = (flight.bayKeys || []).map((bayKey) => ({
      bayKey,
      ...(effectiveMappings.byBayKey[bayKey] || {
        status: 'unmapped',
        effectiveZoneIds: [],
      }),
    }));

    flightsOut.push({
      ...flight,
      mappedZoneIds: mappedZones,
      mappedZoneNames,
      mappingStatuses,
      matchedEquipmentCount: flightMatches.length,
      matches: flightMatches,
    });
  });

  const flightsById = {};
  flightsOut.forEach((flight) => {
    flightsById[flight.id] = flight;
  });

  const equipmentById = {};
  matches.forEach((match) => {
    if (!equipmentById[match.assetId]) {
      equipmentById[match.assetId] = {
        assetId: match.assetId,
        assetCode: match.assetCode,
        flightIds: new Set(),
        polygonFlightIds: new Set(),
        serviceOnlyFlightIds: new Set(),
        matchedFlights: [],
      };
    }

    const item = equipmentById[match.assetId];
    item.flightIds.add(match.flightId);
    if (match.matchChannel === 'polygon') item.polygonFlightIds.add(match.flightId);

    item.matchedFlights.push({
      flightId: match.flightId,
      arrFlightNo: flightsById[match.flightId]?.arrFlightNo || '',
      depFlightNo: flightsById[match.flightId]?.depFlightNo || '',
      bayKeys: flightsById[match.flightId]?.bayKeys || [],
      matchChannel: match.matchChannel,
      matchedZoneName: match.matchedZoneName,
      overlapSec: match.overlapSec,
    });
  });

  Object.values(equipmentById).forEach((item) => {
    item.flightIds.forEach((flightId) => {
      if (!item.polygonFlightIds.has(flightId)) item.serviceOnlyFlightIds.add(flightId);
    });
  });

  const equipment = Object.values(equipmentById)
    .map((item) => ({
      assetId: item.assetId,
      assetCode: item.assetCode,
      flightsHandled: item.flightIds.size,
      polygonFlights: item.polygonFlightIds.size,
      serviceOnlyFlights: item.serviceOnlyFlightIds.size,
      matchedFlights: item.matchedFlights,
    }))
    .sort((a, b) => a.assetCode.localeCompare(b.assetCode));

  const matchedFlightIdSet = new Set(matches.map((m) => m.flightId));
  const flightsMatched = matchedFlightIdSet.size;
  const flightsImported = flightsOut.length;
  const coveragePct = flightsImported > 0 ? (flightsMatched / flightsImported) * 100 : 0;

  const polygonMatches = matches.filter((m) => m.matchChannel === 'polygon').length;
  const serviceMatches = matches.filter((m) => m.matchChannel === 'service').length;

  const unmappedBays = effectiveMappings.rows
    .filter((row) => row.status !== 'mapped')
    .map((row) => ({
      bayKey: row.bayKey,
      status: row.status,
      autoZoneIds: row.autoZoneIds,
      autoZoneNames: row.autoZoneNames,
    }));

  return {
    summary: {
      flightsImported,
      flightsMatched,
      equipmentUsed: equipment.length,
      equipmentFlightMatches: matches.length,
      polygonMatches,
      serviceMatches,
      coveragePct,
    },
    coverage: {
      matchedFlights: flightsMatched,
      totalFlights: flightsImported,
      coveragePct,
    },
    flights: flightsOut,
    equipment,
    matches,
    unmappedBays,
    bayMappings: effectiveMappings.rows,
  };
};
